/**
 * Pure decision logic for the media-page player/processing state. No React /
 * Redux / I/O, so it's unit-tested directly. Assumes nothing about whether
 * Camunda variables, child objects or the SMIL exist at a given time.
 *
 * Two distinct key spaces — kept deliberately separate:
 *   - Content-state (is THIS object done/failed?) is keyed by the item itself
 *     (its @id is its identity). It reads `output_state`, the media_output_
 *     workflow state now exposed as catalog metadata on every content item.
 *   - Camunda progress (which task/profile bar is this?) is keyed by
 *     `camundaProgressKey`, because Camunda's progress blobs are keyed by task
 *     ("transcribe"/"translate") and profile ("360p"/"Audio"), never by @id.
 */

// Relative, not the `volto-base-addon/…` alias: this module is unit-tested and
// plain `npx jest` has no alias resolution.
import { tokenOf } from './vocabulary';

const OUTPUT_TYPES = ['transcoding', 'subtitle'];

// The media_output_workflow state of a content item ('inactive' | 'in_progress'
// | 'completed' | 'failed'), or null when unknown.
export const outputStateOf = (item) => tokenOf(item?.output_state);

export const isTerminalState = (state) =>
  state === 'completed' || state === 'failed';

/**
 * Camunda progress key for an item — used ONLY to match Camunda's progress
 * blobs (keyed by task/profile) so a terminal item's bar can be hidden. Never
 * used for content-state identity (that is the item's @id).
 *
 * Transcoding → profile title ("360p", "Audio").
 * Subtitle    → task token ("transcribe", "translate").
 *
 * Returns null when the task is unknown. We deliberately do NOT guess the task
 * from the @id: without a real `subtitle_task` we cannot honestly map the item
 * to a Camunda bar, so we fail explicitly instead of relying on the id naming
 * convention.
 */
export const camundaProgressKey = (item) => {
  if (item['@type'] === 'transcoding') {
    return item['title'] ?? null;
  }
  if (item['@type'] === 'subtitle') {
    return tokenOf(item['subtitle_task']);
  }
  return null;
};

// The transcoding/subtitle children of a content object (defensive on shape).
const outputItems = (items) =>
  (items || []).filter((i) => OUTPUT_TYPES.includes(i['@type']));

/**
 * Items that have failed processing — derived directly from content metadata
 * (`output_state`), with no Camunda data and no @workflow calls. Returns the
 * items themselves so the caller can build display labels. Available on any
 * content load, no polling required.
 */
export const deriveFailedItems = (items) =>
  outputItems(items).filter((i) => outputStateOf(i) === 'failed');

/**
 * Terminal Camunda progress keys — used to hide the progress bar of a task or
 * profile that is already done. Conservative on key collisions: a key is only
 * "completed"/"failed" when ALL items sharing that key agree and are terminal.
 * If any item with a given key is still non-terminal, the key is neither, so
 * its bar keeps showing (safe, slightly pessimistic — matches the audited
 * behaviour for duplicate keys like two "transcribe" subtitles).
 */
export const deriveTerminalProgressKeys = (items) => {
  const statesByKey = new Map();
  for (const item of outputItems(items)) {
    const key = camundaProgressKey(item);
    if (!key) continue;
    const list = statesByKey.get(key) || [];
    list.push(outputStateOf(item));
    statesByKey.set(key, list);
  }

  const completedItems = [];
  const failedItems = [];
  for (const [key, states] of statesByKey) {
    if (states.every((s) => s === 'completed')) {
      completedItems.push(key);
    } else if (states.every((s) => isTerminalState(s))) {
      failedItems.push(key);
    }
    // else: at least one non-terminal → keep the bar visible
  }
  return { completedItems, failedItems };
};

// True when no playable source can still appear. A source only comes from a
// completed transcoding, so this holds when every transcoding is terminal and
// none completed (all failed) — the caller should stop waiting and say so.
export const deriveNoSourceComing = ({
  items,
  hasPlayableSource,
  isLiveStream,
}) => {
  if (hasPlayableSource || isLiveStream) {
    return false;
  }
  const transcodings = (items || []).filter(
    (item) => item['@type'] === 'transcoding',
  );
  if (transcodings.length === 0) {
    return false;
  }
  const states = transcodings.map(outputStateOf);
  const anyCompleted = states.some((s) => s === 'completed');
  const allTerminal = states.every((s) => isTerminalState(s));
  return !anyCompleted && allTerminal;
};

// Coarse player state. INITIALIZED overrides NOT_INITIALIZED so live (no SMIL
// id but a live_stream_url that produced sources) still mounts.
export const derivePlayerState = ({
  isReady,
  dataHasKeys,
  objectId,
  playerDataFailed,
}) => {
  let state = 'UNSET';
  if (isReady) {
    state = 'WAITING';
  }
  if (objectId === null || objectId === '' || objectId === undefined) {
    state = 'NOT_INITIALIZED';
  }
  if (dataHasKeys && isReady) {
    state = 'INITIALIZED';
  }
  if (playerDataFailed) {
    state = 'FAILED';
  }
  return state;
};

// Known video transcoding profile titles. Audio-only profiles are excluded.
const VIDEO_PROFILES = ['360p', '720p', '1080p', '2160p'];

/**
 * Returns true when the player should NOT mount yet because only audio-only
 * transcodings have completed, while video transcodings still exist and are
 * in progress. This prevents showing an audio-only player for a video upload.
 *
 * Reads each item's `output_state` directly (functional now that the workflow
 * state is exposed as catalog metadata).
 *
 * Returns false (allow mount) when:
 *   - There are no transcodings at all
 *   - At least one video transcoding has completed
 *   - All video transcodings are terminal (nothing more is coming)
 *   - No video profile transcodings exist (pure audio content)
 *   - No audio-only transcoding has completed (no source to mount on anyway)
 */
export const shouldSuppressAudioOnlyMount = (items) => {
  const transcodings = (items || []).filter(
    (item) => item['@type'] === 'transcoding',
  );
  if (transcodings.length === 0) return false;

  const videoTranscodings = transcodings.filter((item) =>
    VIDEO_PROFILES.includes(item.title),
  );
  // No video profiles exist — this is pure audio content, allow mount.
  if (videoTranscodings.length === 0) return false;

  const hasCompletedVideo = videoTranscodings.some(
    (item) => outputStateOf(item) === 'completed',
  );
  if (hasCompletedVideo) return false;

  // All video transcodings are terminal — nothing video is coming, allow mount.
  const allVideoTerminal = videoTranscodings.every((item) =>
    isTerminalState(outputStateOf(item)),
  );
  if (allVideoTerminal) return false;

  // Only suppress if an audio-only transcoding actually completed (creating a
  // source). If nothing completed yet, there's no source to mount on anyway.
  const audioTranscodings = transcodings.filter(
    (item) => !VIDEO_PROFILES.includes(item.title),
  );
  const hasCompletedAudio = audioTranscodings.some(
    (item) => outputStateOf(item) === 'completed',
  );
  if (!hasCompletedAudio) return false;

  // Audio completed, video still in progress. Suppress mount.
  return true;
};

const isObject = (v) => v != null && typeof v === 'object';
const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

// JSON.parse that never throws — the Camunda blob is untrusted, so a single
// malformed value must not crash the render.
export const safeJsonParse = (value, fallback = null) => {
  if (typeof value !== 'string') {
    return fallback;
  }
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

// Parse the (untrusted, possibly partial/malformed) Camunda blob into UI-ready
// progress; terminal items are excluded. Never throws.
//
// progressVariables is keyed by process execution ID — each entry may carry
// its own transcriptionProgress. We merge across all processes so concurrent
// subtitle generations all show their bars.
//
// completedKeys / failedKeys are terminal Camunda progress keys (task/profile),
// used only to drop bars for work that is already done.
export const parseProgressVariables = (
  progressVariables,
  completedKeys = [],
  failedKeys = [],
) => {
  let transcodingProgress = null;
  const transcriptionEntries = [];
  let duration = null;
  let isProcessing = false;
  let faceblurProgress = null;

  const isTerminal = (key) =>
    completedKeys.includes(key) || failedKeys.includes(key);

  if (isObject(progressVariables)) {
    const transcodingProgressRaw = {};

    for (const key of Object.keys(progressVariables)) {
      const entry = progressVariables[key];
      if (!isObject(entry)) {
        continue; // ignore malformed / null entries
      }

      if (hasOwn(entry, 'transcodingProgress')) {
        const progressObject = safeJsonParse(entry.transcodingProgress, null);
        if (isObject(progressObject)) {
          for (const profile of Object.keys(progressObject)) {
            const p = progressObject[profile];
            if (isObject(p)) {
              transcodingProgressRaw[profile] = {
                current_time: p.current_time,
              };
            }
          }
        }
      }
      if (hasOwn(entry, 'duration')) {
        duration = entry.duration;
      }
      if (hasOwn(entry, 'isProcessing')) {
        isProcessing = !!entry.isProcessing;
      }
      if (hasOwn(entry, 'faceblurProgress')) {
        const fb = safeJsonParse(entry.faceblurProgress, null);
        if (isObject(fb)) {
          faceblurProgress = fb;
        }
      }
      if (
        hasOwn(entry, 'transcriptionProgress') &&
        typeof entry.transcriptionProgress === 'string'
      ) {
        const parsed = safeJsonParse(entry.transcriptionProgress, null);
        if (isObject(parsed)) {
          for (const task of Object.keys(parsed)) {
            if (!isTerminal(task) && isObject(parsed[task])) {
              transcriptionEntries.push({ task, ...parsed[task] });
            }
          }
        }
      }
    }

    // Emit a percentage only with a usable duration/current time (no NaN bar).
    const durationNum = Number(duration);
    const hasValidDuration = Number.isFinite(durationNum) && durationNum > 0;
    transcodingProgress = {};
    Object.keys(transcodingProgressRaw).forEach((key) => {
      if (isTerminal(key)) {
        return;
      }
      const current = parseFloat(transcodingProgressRaw[key]['current_time']);
      if (hasValidDuration && Number.isFinite(current)) {
        transcodingProgress[key] = Math.round((current / durationNum) * 100);
      }
    });
  }

  // Legacy shape: also expose a keyed object for backward compat with
  // existing view code. If multiple processes report the same task key,
  // all are visible in the entries array.
  const transcriptionProgress = {};
  for (const entry of transcriptionEntries) {
    transcriptionProgress[entry.task] = entry;
  }

  return {
    transcodingProgress,
    transcriptionProgress,
    transcriptionEntries,
    isProcessing,
    faceblurProgress,
  };
};

/**
 * Derive the generation status for the view layer.
 *
 * Returns one of:
 *   'idle'       — no generation in progress (or wrong page, or cleared)
 *   'pending'    — generation started, keep polling, show spinner
 *   'timed_out'  — waited too long, child never appeared, show warning
 *
 * Pure function — all guardrails inline, never throws.
 */
export const deriveGenerationStatus = ({
  generationUid,
  generationStartedAt,
  timeoutMs,
  contentUid,
  normalPollWouldRun,
}) => {
  if (!generationUid || !generationStartedAt) return 'idle';
  if (generationUid !== contentUid) return 'idle';
  if (normalPollWouldRun) return 'idle';
  if (Date.now() - generationStartedAt > timeoutMs) return 'timed_out';
  return 'pending';
};
